import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Button,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Block } from '@mui/icons-material';
import {
  annualizedMiles,
  fleetRisk,
  formatUsd,
} from '../../../utils/vehicleComparison';

export interface CarSummary {
  id: string;
  vin: string;
  make: string;
  model: string;
  trim: string;
  version: string;
  photos: string[];
  year: number;
  mileage: number;
  /** Dealer's advertised price. */
  listPrice?: number | null;
  msrp?: number | null;
  /** Marketcheck `carfax_1_owner` — a cleaner binary than `carfax_clean_title` (findings §2). */
  carfax1Owner?: boolean | null;
}

interface CarListingRowProps {
  car: CarSummary;
  dealer: {
    name: string;
    city: string;
    state: string;
    dist: number;
  };
  isSelected: boolean;
  selectionDisabled?: boolean;
  onSelect?: (carId: string) => void;
  onShowDetails?: (car: CarSummary) => void;
  slim?: boolean;
}

const CarListingRow = ({
  car,
  dealer,
  isSelected,
  selectionDisabled = false,
  onSelect,
  onShowDetails,
  slim = false,
}: CarListingRowProps) => {
  return (
    <Card
      sx={{
        display: 'flex',
        height: slim ? undefined : '250px',
        transition: 'transform 0.2s, box-shadow 0.2s',
        ...(!slim && {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }),
        cursor: onShowDetails ? 'pointer' : 'default',
        position: 'relative',
        bgcolor: 'white',
      }}
      onClick={() => onShowDetails?.(car)}
      elevation={slim ? 0 : 2}
    >
      <Grid container>
        <Grid item xs={slim ? 3 : 5}>
          <Box sx={{ height: '100%', position: 'relative' }}>
            {car.photos?.[0] ? (
              <img
                src={car.photos[0]}
                alt={`${car.year} ${car.make} ${car.model}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                sx={{ width: '100%', height: '100%', minHeight: 120 }}
              >
                <Block sx={{ width: '40%', height: '40%' }} />
                <Typography variant="body2">No image</Typography>
              </Stack>
            )}
          </Box>
        </Grid>

        <Grid item xs={slim ? 9 : 7}>
          <CardContent
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 3,
              position: 'relative',
            }}
          >
            {!slim && onSelect && (
              <Button
                variant="contained"
                size="small"
                disabled={!isSelected && selectionDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(car.id);
                }}
                startIcon={
                  isSelected ? <CheckCircleIcon /> : <AddCircleOutlineIcon />
                }
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: isSelected ? 'success.main' : 'primary.main',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? 'success.dark'
                      : 'primary.dark',
                  },
                }}
              >
                {isSelected ? 'Selected' : 'Select'}
              </Button>
            )}
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              {car.year} {car.make} {car.model}
            </Typography>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: 'medium' }}
            >
              {car.version || car.trim}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: 'flex',
                gap: 3,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="h6">{formatUsd(car.listPrice)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Listed price
                </Typography>
              </Box>
              {car.msrp ? (
                <Box>
                  <Typography variant="body1">{formatUsd(car.msrp)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    MSRP
                  </Typography>
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon color="action" />
                <Typography variant="body1">
                  {car.mileage?.toLocaleString()} miles
                </Typography>
              </Box>
            </Box>

            {(fleetRisk(car.mileage, car.year) === 'high' ||
              car.carfax1Owner === true) && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                {fleetRisk(car.mileage, car.year) === 'high' && (
                  <Tooltip
                    title={`~${Math.round(
                      annualizedMiles(car.mileage, car.year)
                    ).toLocaleString()} mi/yr; rental fleets run 30–37k, personal use 12–19k`}
                  >
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label="High miles for age"
                    />
                  </Tooltip>
                )}
                {car.carfax1Owner === true && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label="1 owner"
                  />
                )}
              </Stack>
            )}

            {!slim && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mt: 'auto',
                }}
              >
                <Stack direction="row" gap={1}>
                  <LocationOnIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {dealer.name}
                    <br />
                    {dealer.city}, {dealer.state}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {dealer.dist} miles
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );
};

export default CarListingRow;
